// Allocation rows (deal + quantity) shared by the Mark split dialog and the
// manual entry form. The client-side check mirrors the server rule (sum of
// active allocations <= event quantity); the server stays authoritative (409).
import { compareDecimal, formatDecimal, isDecimalString, subDecimal, sumDecimals } from '../lib/decimal';
import type { DealRef } from '../api/model';

export interface AllocRow {
  key: string;
  dealId: number | null;
  qty: string;
}

export interface AllocValidation {
  ok: boolean;
  sum: string;
  remainder: string | null;
  error: string | null;
}

export function validateAllocations(rows: AllocRow[], total: string): AllocValidation {
  const bad = rows.find((r) => r.dealId === null || !isDecimalString(r.qty) || compareDecimal(r.qty, '0') <= 0);
  const valid = rows.filter((r) => isDecimalString(r.qty));
  const sum = sumDecimals(valid.map((r) => r.qty));
  if (bad) return { ok: false, sum, remainder: null, error: 'Minden sorhoz ügylet és pozitív mennyiség kell.' };
  if (compareDecimal(sum, total) > 0) {
    return { ok: false, sum, remainder: null, error: `Az allokációk összege (${formatDecimal(sum)}) nem lépheti túl az esemény mennyiségét (${formatDecimal(total)}).` };
  }
  return { ok: true, sum, remainder: subDecimal(total, sum), error: null };
}

let seq = 0;
export const newAllocRow = (dealId: number | null = null, qty = ''): AllocRow => ({ key: `a${++seq}`, dealId, qty });

export function AllocationEditor({
  rows,
  onChange,
  deals,
  total,
  asset,
}: {
  rows: AllocRow[];
  onChange: (rows: AllocRow[]) => void;
  deals: DealRef[];
  total: string;
  asset: string;
}) {
  const v = validateAllocations(rows, total);
  const set = (key: string, patch: Partial<AllocRow>) => onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  return (
    <div className="stack">
      {rows.map((r, i) => (
        <div className="alloc-row" key={r.key}>
          <div className="field">
            <label htmlFor={`alloc-deal-${r.key}`}>Ügylet {i + 1}</label>
            <select id={`alloc-deal-${r.key}`} className="input" value={r.dealId ?? ''} onChange={(e) => set(r.key, { dealId: e.target.value ? Number(e.target.value) : null })}>
              <option value="">Válassz ügyletet…</option>
              {deals.map((d) => (
                <option key={d.id} value={d.id}>
                  #{d.id} {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor={`alloc-qty-${r.key}`}>Mennyiség ({asset})</label>
            <input id={`alloc-qty-${r.key}`} className="input mono" inputMode="decimal" value={r.qty} onChange={(e) => set(r.key, { qty: e.target.value.trim().replace(',', '.') })} />
          </div>
          <button type="button" className="btn small" aria-label={`Sor törlése: ${i + 1}`} onClick={() => onChange(rows.filter((x) => x.key !== r.key))}>
            Törlés
          </button>
        </div>
      ))}
      <div className="row-wrap" style={{ alignItems: 'center' }}>
        <button type="button" className="btn small" onClick={() => onChange([...rows, newAllocRow()])}>
          Sor hozzáadása
        </button>
        <span className="small muted">
          Esemény mennyisége: <span className="mono">{formatDecimal(total)} {asset}</span>
          {v.remainder !== null ? (
            <>
              {' '}
              · nem allokált: <span className="mono">{formatDecimal(v.remainder)}</span>
            </>
          ) : null}
        </span>
      </div>
      {v.error ? (
        <div className="notice err small" role="alert">
          {v.error}
        </div>
      ) : null}
    </div>
  );
}
